import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {
  ClientHints,
  DeviceSupportResult,
  LogDeviceSupportRequest,
  NavigatorUaData,
} from '../models/client-hints.model';

const CLIENT_HINTS_URL = '/api/v1/device-support/client-hints';
const USER_AGENT_URL = '/api/v1/device-support/user-agent';
const LOG_URL = '/api/v1/device-support/log';

const HIGH_ENTROPY_HINTS = [
  'architecture',
  'model',
  'platform',
  'platformVersion',
  'uaFullVersion',
];

const FAIL_OPEN: DeviceSupportResult = {
  isBrowserSupported: true,
  isDeviceSupported: true,
  isOperatingSystemSupported: true,
};

@Injectable({ providedIn: 'root' })
export class DeviceSupportService {
  private readonly http = inject(HttpClient);

  checkByClientHints(clientHints: ClientHints): Observable<DeviceSupportResult> {
    return this.http
      .post<DeviceSupportResult>(CLIENT_HINTS_URL, clientHints)
      .pipe(catchError(() => of(FAIL_OPEN)));
  }

  checkByUserAgent(): Observable<DeviceSupportResult> {
    return this.http
      .get<DeviceSupportResult>(USER_AGENT_URL)
      .pipe(catchError(() => of(FAIL_OPEN)));
  }

  logResult(request: LogDeviceSupportRequest): Observable<void> {
    return this.http
      .post<void>(LOG_URL, request)
      .pipe(catchError(() => of(void 0)));
  }

  getClientHints(): Observable<ClientHints | null> {
    const uaData = (navigator as Navigator & { userAgentData?: NavigatorUaData }).userAgentData;
    if (!uaData?.getHighEntropyValues) {
      return of(null);
    }

    try {
      return from(
        uaData.getHighEntropyValues(HIGH_ENTROPY_HINTS).then(
          (highEntropy): ClientHints => ({
            architecture: highEntropy.architecture ?? '',
            brands: uaData.brands ?? [],
            mobile: uaData.mobile ?? false,
            model: highEntropy.model ?? '',
            platform: highEntropy.platform ?? uaData.platform ?? '',
            platformVersion: highEntropy.platformVersion ?? '',
            uaFullVersion: highEntropy.uaFullVersion ?? '',
          }),
          () => null,
        ),
      );
    } catch {
      return of(null);
    }
  }
}
